import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FullCV,
  Input,
  isInternalAiNote,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2 } from 'lucide-react';
import React from 'react';
import { ArrayPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormArray } from '@/features/common/hooks/useFormArray';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import {
  complementaryInfoSchema,
  type ComplementaryInfoFormValues as CIFormValues,
} from '../../schemas/complementaryInfo.schema';
import FormLayout from './FormLayout';
import NumberedItem from './NumberedItem';

interface ComplementaryInfoFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const ComplementaryInfoForm: React.FC<ComplementaryInfoFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
}) => {
  const { t } = useTranslation();

  const form = useForm<CIFormValues>({
    resolver: zodResolver(complementaryInfoSchema),
    defaultValues: initialData
      ? {
          hasRQTH: initialData.hasRQTH ?? false,
          complementaryMentions: initialData.complementaryMentions?.filter(
            m => !isInternalAiNote(m),
          ).length
            ? initialData.complementaryMentions.filter(m => !isInternalAiNote(m))
            : [''],
        }
      : {
          hasRQTH: false,
          complementaryMentions: [''],
        },
  });

  const { fields, canAdd, handleAdd, handleRemove } = useFormArray<CIFormValues>({
    form,
    name: 'complementaryMentions' as ArrayPath<CIFormValues>,
    maxItems: 10,
    minItems: 0,
  });

  const handleSubmit = (data: CIFormValues) => {
    onSubmit({
      ...initialData,
      hasRQTH: data.hasRQTH,
      complementaryMentions: data.complementaryMentions.filter(m => m.trim() !== ''),
    } as FullCV);
  };

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'complementaryInfo');

  return (
    <FormLayout
      title={t('Forms.ComplementaryInfo.title')}
      onCancel={onCancel}
      onSubmit={form.handleSubmit(handleSubmit)}
      showAddButton={canAdd}
      onAdd={() => handleAdd('')}
      addButtonText={t('Forms.ComplementaryInfo.addMention')}
    >
      <Form {...form}>
        <form className="space-y-6 pb-20">
          <FormField
            control={form.control}
            name="hasRQTH"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      className="resize-none"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormLabel>{t('Forms.ComplementaryInfo.hasRQTH')}</FormLabel>
                  {field.value && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="shrink-0 text-destructive hover:text-destructive/90 h-12 w-12"
                      onClick={() => field.onChange(false)}
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {fields.map((field, index) => (
            <NumberedItem
              key={field.id}
              index={index}
              showDelete={true}
              className="items-center"
              onDelete={() => handleRemove(index)}
            >
              <FormField
                control={form.control}
                name={`complementaryMentions.${index}`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="h-12 w-full min-w-0"
                        placeholder={t('Forms.ComplementaryInfo.mentionPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </NumberedItem>
          ))}
        </form>
      </Form>
    </FormLayout>
  );
};

export default ComplementaryInfoForm;
